import { useEffect, useMemo, type ReactNode } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { buildDestinations, buildSections, type GuardedNavItem } from './navigation'
import AppShell, { type NavEntry } from '../ui/patterns/AppShell'
import SectionTabs from '../ui/patterns/SectionTabs'
import { getAppEnvironment } from '../lib/env'
import { BRAND_PRODUCT, BRAND_SHELL_TAGLINE, documentTitle } from '../lib/brand'
import { useSession } from './providers/sessionContext'

/**
 * The shell every signed-in route renders inside.
 *
 * Navigation is filtered by permission, which is a usability decision and not a
 * security one. Hiding a link stops a viewer wondering why administration does
 * nothing; it stops nobody who types the URL. The guard behind the route and
 * the policy behind the data are what actually refuse, and both are tested
 * independently of what the sidebar shows.
 */


function roleLabel(role: string | null, isPlatformAdmin: boolean): string {
  if (isPlatformAdmin && role === null) return 'Platform administrator'
  const base = role === null ? 'No organisation' : role.charAt(0).toUpperCase() + role.slice(1)
  return isPlatformAdmin ? `${base} and platform administrator` : base
}


export default function AuthenticatedShell({ children }: { children: ReactNode }) {
  const session = useSession()
  const navigate = useNavigate()
  const location = useLocation()

  const isAdministrator = session.role === 'owner' || session.role === 'admin'

  /*
   * One flat list of destinations, each carrying the permission that decides
   * whether it is offered, and separately a grouping that decides how it is
   * presented.
   *
   * Kept apart on purpose. Permission is a property of a screen; grouping is a
   * property of the menu. Folding them together would let a future change to
   * the menu shape quietly change who is offered what.
   */
  const destinations: readonly GuardedNavItem[] = buildDestinations(
    { ...session, linkedEmployee: session.linkedEmployeeId !== null },
    isAdministrator,
  )

  /*
   * Timesheets is deliberately absent.
   *
   * It was listed and greyed out with "Not available yet". Telling a paying
   * customer about a section they cannot use advertises the gap rather than the
   * product. The route and its code are untouched; it returns to this list when
   * it does something.
   */

  /*
   * Eight business areas, not twenty screens.
   *
   * The sidebar answers "which part of the business am I in" and the tab strip
   * answers "which part of that am I looking at". Both are built from the same
   * destination list, with the same `visible` flags decided in the same place,
   * so grouping changes what a reader sees and never what they may reach.
   */
  const sections = useMemo(() => buildSections(destinations), [destinations])

  const entries: readonly NavEntry[] = sections.map((section) => ({
    id: section.id,
    icon: section.icon,
    label: section.label,
    available: true,
  }))
  const visible = destinations.filter((item) => item.visible)

  /*
   * Exact match, then the longest path a detail route sits under.
   *
   * This was an exact comparison only, so every detail route fell through to
   * the fallback and the sidebar highlighted Dashboard while somebody was
   * plainly looking at a job. The browser tab said Dashboard too, because the
   * title is derived from the same lookup.
   *
   * The prefix needs the trailing slash. Without it `/jobs` would match
   * `/jobs-archive`, and the dashboard's own `/` would match every page in the
   * product. Longest wins so `/reports/jobs/:id` resolves to Job costs rather
   * than to the Reports group's first child.
   */
  const activeItem =
    visible.find((item) => item.path === location.pathname) ??
    visible
      .filter((item) => location.pathname.startsWith(`${item.path}/`))
      .sort((a, b) => b.path.length - a.path.length)[0]
  const active = activeItem?.id ?? 'dashboard'

  /*
   * Which area the active screen belongs to, and the screens beside it.
   *
   * Found from the destination rather than from the path, so a detail route
   * such as `/products/:id` inherits its parent's section through the same
   * longest-prefix match the sidebar highlight already used.
   */
  const activeSection = useMemo(
    () =>
      sections.find(
        (section) =>
          section.id === active ||
          section.children.some((child) => child.id === active),
      ) ?? null,
    [sections, active],
  )

  const sectionTabs =
    activeSection === null || activeSection.children.length < 2 ? null : (
      <SectionTabs
        label={activeSection.label}
        items={activeSection.children}
        activeId={active}
        onNavigate={(path) => navigate(path)}
      />
    )

  /*
   * The browser title, derived from the navigation rather than set per page.
   *
   * One place to maintain, and it cannot drift from what the sidebar calls the
   * screen. A route with no navigation entry, such as a detail screen, falls
   * back to the product name rather than inheriting the previous page's title,
   * which is the bug this replaces: every tab said "gdi-labour".
   */
  useEffect(() => {
    document.title = documentTitle(activeItem?.label)
  }, [activeItem?.label])

  return (
    <AppShell
      identity={{
        product: BRAND_PRODUCT,
        tagline: BRAND_SHELL_TAGLINE,
        organisation: session.membership?.organisationName ?? 'No organisation',
        environment: getAppEnvironment(),
        userName: session.fullName ?? session.email ?? 'Signed in',
        userRole: roleLabel(session.role, session.isPlatformAdmin),
        ...(session.email === null || session.email === undefined
          ? {}
          : { userEmail: session.email }),
      }}
      navItems={entries}
      onSignOut={() => void session.signOut()}
      activeNavId={activeSection?.id ?? active}
      onNavigate={(id) => {
        /*
         * A section goes to its first screen. That is the screen the section is
         * named for when it has only one, and the leftmost tab when it has
         * several, so pressing an area never lands on a blank shell.
         */
        const section = sections.find((entry) => entry.id === id)
        if (section !== undefined) navigate(section.path)
      }}
      {...(sectionTabs === null ? {} : { sectionTabs })}
    >
      {children}
    </AppShell>
  )
}
